import { useRef, useCallback, useState } from 'react';

/**
 * useStreamingSTT Hook (Speech-to-Text)
 *
 * Abstracts STT implementation - can be swapped between:
 * - Web Speech API (current - non-streaming but fast)
 * - Deepgram (streaming)
 * - OpenAI Whisper (streaming)
 * - Google Cloud STT (streaming)
 *
 * For now, using Web Speech API with streaming-like interface
 * Can be easily replaced with real streaming STT
 */
export const useStreamingSTT = (options = {}) => {
  const {
    onPartialTranscript,
    onFinalTranscript,
    onError,
    language = 'en-US'
  } = options;

  const recognitionRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const audioChunksRef = useRef([]);

  /**
   * Initialize STT engine
   */
  const init = useCallback(() => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      console.error('❌ Speech Recognition not supported');
      onError?.('Speech Recognition not supported in this browser');
      return false;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;  // Enable partial transcripts
    recognitionRef.current.lang = language;
    recognitionRef.current.maxAlternatives = 1;

    recognitionRef.current.onresult = (event) => {
      try {
        const lastResultIndex = event.results.length - 1;
        const result = event.results[lastResultIndex];
        const transcript = result[0].transcript;
        const confidence = result[0].confidence || 0.9; // Safari may not provide confidence

        if (result.isFinal) {
          console.log('📝 Final transcript:', transcript, `(confidence: ${(confidence * 100).toFixed(0)}%)`);
          onFinalTranscript?.(transcript, confidence);
        } else {
          console.log('📝 Partial transcript:', transcript);
          onPartialTranscript?.(transcript);
        }
      } catch (error) {
        console.error('❌ Error processing STT result:', error);
        onError?.(error.message);
      }
    };

    recognitionRef.current.onerror = (event) => {
      console.error('❌ STT error:', event.error);

      // Filter out benign errors that don't need user notification
      const benignErrors = ['no-speech', 'aborted'];
      if (benignErrors.includes(event.error)) {
        return;  // Don't report benign errors
      }

      // Report critical errors to parent
      if (event.error === 'network' || event.error === 'service-not-allowed' || event.error === 'not-allowed') {
        onError?.(event.error);
      }

      // Auto-restart on network errors (Safari/Chrome compatibility workaround)
      if (event.error === 'network' && isListening) {
        console.log('🔄 Auto-restarting STT after network error');
        setTimeout(() => {
          if (recognitionRef.current) {
            try {
              recognitionRef.current.start();
              console.log('✓ STT restarted successfully');
            } catch (restartError) {
              console.error('❌ Failed to restart STT:', restartError);
            }
          }
        }, 500);
      }
    };

    recognitionRef.current.onend = () => {
      console.log('🔚 STT session ended');
      setIsListening(false);
    };

    return true;
  }, [language, onPartialTranscript, onFinalTranscript, onError]);

  /**
   * Start listening / streaming
   */
  const startSTT = useCallback(() => {
    if (!recognitionRef.current && !init()) {
      return;
    }

    try {
      // Safari fix: Check if already listening
      if (isListening) {
        console.log('⚠️ STT already running, skipping start');
        return;
      }

      recognitionRef.current.start();
      setIsListening(true);
      audioChunksRef.current = [];
      console.log('🎙️ STT started');
    } catch (error) {
      if (error.message && error.message.includes('already started')) {
        console.log('✓ STT already running');
        setIsListening(true);
      } else {
        console.error('❌ Error starting STT:', error);
        onError?.(error.message);
      }
    }
  }, [init, onError, isListening]);

  /**
   * Send audio chunk (for real streaming STT services)
   * Currently not used with Web Speech API, but interface is ready
   */
  const sendAudioChunk = useCallback((chunk) => {
    // For Web Speech API, this is not used
    // For Deepgram/Whisper, send chunk over WebSocket here
    audioChunksRef.current.push(chunk);
  }, []);

  /**
   * Stop listening
   */
  const stopSTT = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
        console.log('🛑 STT stopped');
      } catch (error) {
        console.error('Error stopping STT:', error);
      }
    }
    setIsListening(false);
  }, []);

  /**
   * Abort (force stop immediately)
   */
  const abortSTT = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
        console.log('⚠️ STT aborted');
      } catch (error) {
        console.error('Error aborting STT:', error);
      }
    }
    setIsListening(false);
    audioChunksRef.current = [];
  }, []);

  return {
    startSTT,
    stopSTT,
    abortSTT,
    sendAudioChunk,
    isListening
  };
};

/**
 * Example integration with Deepgram (for future):
 *
 * const deepgramSocket = new WebSocket('wss://api.deepgram.com/v1/listen?...');
 *
 * const sendAudioChunk = (chunk) => {
 *   if (deepgramSocket.readyState === WebSocket.OPEN) {
 *     deepgramSocket.send(chunk);
 *   }
 * };
 *
 * deepgramSocket.onmessage = (message) => {
 *   const data = JSON.parse(message.data);
 *   if (data.is_final) {
 *     onFinalTranscript(data.channel.alternatives[0].transcript);
 *   } else {
 *     onPartialTranscript(data.channel.alternatives[0].transcript);
 *   }
 * };
 */
